import React, { useState, useRef, useEffect } from 'react';
import { parse } from 'papaparse';
import moment from 'moment';
import { addBulkInventoryItems, fetchFutureFlights } from '../../../lib/actions';

const TicketCSVUploadModal = ({ isOpen, onClose, onComplete }) => {
    const [file, setFile] = useState(null);
    const [validationResults, setValidationResults] = useState([]);
    const [summary, setSummary] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [validationMessage, setValidationMessage] = useState({ type: null, message: null });
    const [futureFlights, setFutureFlights] = useState([]);
    const [isLoadingFlights, setIsLoadingFlights] = useState(false);
    const fileInputRef = useRef(null);

    const EXPECTED_CSV_FORMAT = ['flight', 'departure_date', 'departure_time', 'arrival_time', 'price', 'available_tickets'];

    // Fetch future flights when modal opens
    useEffect(() => {
        const loadFlights = async () => {
            try {
                setIsLoadingFlights(true);
                const flights = await fetchFutureFlights();
                setFutureFlights(flights);
            } catch (error) {
                console.error("Error loading flights:", error);
                setValidationMessage({ type: 'error', message: error.message });
            } finally {
                setIsLoadingFlights(false);
            }
        };

        if (isOpen) {
            loadFlights();
        }
    }, [isOpen]);

    const resetForm = () => {
        setFile(null);
        setValidationResults([]);
        setSummary(null);
        setValidationMessage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const validateRow = (rowData, rowIndex) => {
        const errors = [];
        const extractedData = {};

        // Flight number validation
        if (!rowData.flight || typeof rowData.flight !== 'string') {
            errors.push('Invalid flight number');
        }
        extractedData.flight = rowData.flight;

        // Date validation
        const departureDate = moment(rowData.departure_date, 'YYYY-MM-DD', true);
        if (!departureDate.isValid()) {
            errors.push('Invalid departure date (expected YYYY-MM-DD)');
        } else if (departureDate.isBefore(moment(), 'day')) {
            errors.push('Departure date cannot be in the past');
        }
        extractedData.departure_date = rowData.departure_date;

        // Time validations
        if (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(rowData.departure_time)) {
            errors.push('Invalid departure time (expected HH:MM)');
        }
        extractedData.departure_time = rowData.departure_time;

        if (!/^([01]?[0-9]|2[0-3]):[0-5][0-9](?:\s*\(\+1\))?$/.test(rowData.arrival_time)) {
            errors.push('Invalid arrival time (expected HH:MM or HH:MM (+1))');
        }
        extractedData.arrival_time = rowData.arrival_time;

        // Price validation
        const priceValue = parseFloat(rowData.price.toString().replace(/[$,]/g, ''));
        if (isNaN(priceValue)) {
            errors.push('Invalid price (expected number or currency)');
        }
        extractedData.price = rowData.price;

        // Seats validation
        const seatsValue = parseInt(rowData.available_tickets);
        if (isNaN(seatsValue) || seatsValue < 0) {
            errors.push('Invalid available tickets (expected positive number)');
        }
        extractedData.available_tickets = rowData.available_tickets;

        // Check for existing flights
        if (extractedData.flight && extractedData.departure_date && !errors.some(e => e.includes('Departure date'))) {
            const exists = futureFlights.some(
                flight => flight.flightNo === extractedData.flight &&
                    flight.departureDate === extractedData.departure_date
            );
            if (exists) {
                errors.push('Flight already exists');
            }
        }

        return {
            row: rowIndex + 1,
            data: extractedData,
            errors,
            exists: errors.includes('Flight already exists'),
            isPastDate: errors.includes('Departure date cannot be in the past')
        };
    };

    const validateCsv = (data) => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            setValidationMessage({ type: 'error', message: "CSV file is empty" });
            return {
                isValid: false,
                error: "CSV file is empty",
                criticalError: true
            };
        }

        // Check headers
        const firstRow = data[0]?.data || data[0];
        if (!firstRow) {
            setValidationMessage({ type: 'error', message: "Could not read CSV headers" });
            return {
                isValid: false,
                error: "Could not read CSV headers",
                criticalError: true
            };
        }

        const csvHeaders = Object.keys(firstRow).map(header => header.toLowerCase().replace(/\s+/g, '_'));
        const missingHeaders = EXPECTED_CSV_FORMAT.filter(header => !csvHeaders.includes(header.toLowerCase()));

        if (missingHeaders.length > 0) {
            const message = `Missing required columns: ${missingHeaders.join(', ')}`;
            setValidationMessage({ type: 'error', message });
            return {
                isValid: false,
                error: message,
                criticalError: true
            };
        }

        const results = [];
        let hasErrors = false;

        data.forEach((row, index) => {
            const rowData = row.data || row;
            const validated = validateRow(rowData, index);
            results.push(validated);

            if (validated.errors.length > 0) {
                hasErrors = true;
            }
        });

        return {
            isValid: !hasErrors,
            results,
            error: hasErrors ? 'Some records have validation issues' : null,
            isWarning: hasErrors
        };
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
            setValidationMessage({ type: 'error', message: 'Please upload a CSV file (.csv extension)' });
            return;
        }

        setFile(selectedFile);
        setValidationResults([]);
        setSummary(null);
        setValidationMessage(null);
    };

    const handleValidateCSV = async () => {
        if (!file) {
            setValidationMessage({ type: 'error', message: 'Please select a CSV file first' });
            return;
        }

        if (isLoadingFlights) {
            setValidationMessage({ type: 'error', message: 'Still loading existing flights' });
            return;
        }

        setIsUploading(true);
        setValidationMessage(null);

        parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => {
                return header.trim().toLowerCase().replace(/\s+/g, '_');
            },
            complete: (results) => {
                if (!results.data || results.data.length === 0) {
                    setValidationMessage({ type: 'error', message: 'CSV file is empty' });
                    setIsUploading(false);
                    return;
                }

                const validation = validateCsv(results.data);
                if (validation?.isValid || !validation?.criticalError) {
                    const validRecords = validation?.results.filter(r => r.errors.length === 0);
                    const invalidRecords = validation?.results.filter(r => r.errors.length > 0);
                    const existingRecords = validation?.results.filter(r => r.exists);
                    const pastDateRecords = validation?.results.filter(r => r.isPastDate);

                    setValidationResults(validation?.results);
                    setSummary({
                        total: validation?.results.length,
                        valid: validRecords?.length,
                        invalid: invalidRecords?.length,
                        existing: existingRecords?.length,
                        pastDate: pastDateRecords?.length
                    });
                }

                setIsUploading(false);
            },
            error: (error) => {
                setValidationMessage({ type: 'error', message: `Error parsing CSV: ${error.message}` });
                setIsUploading(false);
            }
        });
    };

    const handleAddInventory = async () => {
        if (!validationResults || validationResults.length === 0) return;

        setIsUploading(true);
        try {
            const recordsToUpload = validationResults
                .filter(record => !record.exists && !record.isPastDate && record.errors.length === 0)
                .map(record => ({
                    flightNo: record.data.flight,
                    departureDate: record.data.departure_date,
                    departureTime: record.data.departure_time,
                    arrivalTime: record.data.arrival_time,
                    price: parseFloat(record.data.price.toString().replace(/[$,]/g, '')),
                    availableTickets: parseInt(record.data.available_tickets),
                    createdAt: new Date().toISOString()
                }));

            const successCount = await addBulkInventoryItems(recordsToUpload);

            setValidationMessage({
                type: 'success',
                message: `Successfully uploaded ${successCount} flights`
            });

            setTimeout(() => {
                onComplete(true); // Force refresh
                onClose();
                resetForm();
            }, 1500);
        } catch (error) {
            console.error('Upload failed:', error);
            setValidationMessage({
                type: 'error',
                message: `Error during upload: ${error.message}`
            });
            setSummary(null);
        } finally {
            setIsUploading(false);
        }
    };

    const showValidationSummary = summary && !(validationMessage?.type === 'error');

    return (
        <div className={`modal ${isOpen ? 'show d-block' : ''}`} tabIndex="-1">
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Upload Flight Inventory CSV</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => {
                                onClose();
                                resetForm();
                            }}
                            disabled={isUploading}
                        />
                    </div>
                    <div className="modal-body">
                        {validationMessage && validationMessage.message && (
                            <div className={`alert ${validationMessage.type === 'error' ? 'alert-danger' :
                                validationMessage.type === 'success' ? 'alert-success' : 'alert-warning'
                                }`}>
                                {validationMessage.message}
                            </div>
                        )}

                        {isLoadingFlights && (
                            <div className="alert alert-info">Loading existing flights...</div>
                        )}

                        {!showValidationSummary ? (
                            <>
                                <div className="mb-3">
                                    <label htmlFor="csvFile" className="form-label">
                                        Select CSV File
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        id="csvFile"
                                        accept=".csv"
                                        onChange={handleFileChange}
                                        disabled={isUploading || isLoadingFlights}
                                        ref={fileInputRef}
                                    />
                                    <div className="form-text">
                                        CSV must contain these columns: {EXPECTED_CSV_FORMAT.join(', ')}
                                    </div>
                                </div>

                                <div className="alert alert-info">
                                    <h6>Expected CSV Format:</h6>
                                    <pre className="mb-0">
                                        flight,departure_date,departure_time,arrival_time,price,available_tickets<br />
                                        IZ991,2025-02-08,23:30,05:50 (+1),599.00,10<br />
                                        IZ992,2025-02-09,15:00,09:00 (+1),599.00,10
                                    </pre>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={`alert ${summary.invalid > 0 ? 'alert-warning' : 'alert-success'}`}>
                                    <h5>Validation Summary</h5>
                                    <ul className="mb-0">
                                        <li>Total Records: {summary.total}</li>
                                        <li>Valid Records: {summary.valid}</li>
                                        {summary.existing > 0 && <li>Existing Flights: {summary.existing}</li>}
                                        {summary.pastDate > 0 && <li>Past Date Flights: {summary.pastDate}</li>}
                                        <li>Invalid Records: {summary.invalid}</li>
                                    </ul>
                                </div>

                                {(summary.invalid > 0 || summary.pastDate > 0) && (
                                    <div className="mt-3">
                                        <h6>Issues Found ({summary.invalid + summary.pastDate}):</h6>
                                        <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            <table className="table table-sm table-bordered">
                                                <thead className="sticky-top bg-light">
                                                    <tr>
                                                        <th>Row</th>
                                                        <th>Flight</th>
                                                        <th>Departure Date</th>
                                                        <th>Issues</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {validationResults
                                                        .filter(r => r.errors.length > 0 || r.isPastDate)
                                                        .map((record, i) => (
                                                            <tr key={`invalid-${i}`}>
                                                                <td>{record.row}</td>
                                                                <td>{record.data.flight}</td>
                                                                <td>{record.data.departure_date}</td>
                                                                <td className="text-danger">
                                                                    {record.errors.join(', ')}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {summary.valid > 0 && (
                                    <div className="mt-3">
                                        <h6>Valid Records ({summary.valid}):</h6>
                                        <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            <table className="table table-sm table-bordered">
                                                <thead className="sticky-top bg-light">
                                                    <tr>
                                                        <th>Row</th>
                                                        <th>Flight</th>
                                                        <th>Departure Date</th>
                                                        <th>Departure Time</th>
                                                        <th>Arrival Time</th>
                                                        <th>Price</th>
                                                        <th>Seats</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {validationResults
                                                        .filter(r => r.errors.length === 0 && !r.isPastDate)
                                                        .map((record, i) => (
                                                            <tr key={`valid-${i}`}>
                                                                <td>{record.row}</td>
                                                                <td>{record.data.flight}</td>
                                                                <td>{record.data.departure_date}</td>
                                                                <td>{record.data.departure_time}</td>
                                                                <td>{record.data.arrival_time}</td>
                                                                <td>{record.data.price}</td>
                                                                <td>{record.data.available_tickets}</td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                onClose();
                                resetForm();
                            }}
                            disabled={isUploading}
                        >
                            Cancel
                        </button>

                        {!showValidationSummary ? (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleValidateCSV}
                                disabled={!file || isUploading || isLoadingFlights}
                            >
                                {isUploading ? 'Validating...' : 'Validate CSV'}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={handleAddInventory}
                                disabled={isUploading || summary.valid === 0}
                            >
                                {isUploading ? 'Uploading...' : `Upload ${summary.valid} Valid Records`}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketCSVUploadModal;